import React, { useState, useEffect, useRef } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { Card, Button } from "react-bootstrap";
import {
  getQmeDoctorCaseVolume,
  getQmeDoctorCaseVolumeByID,
  QmeDoctorCaseVolumeByID,
} from "../../../../../services/ActionStatusApi";
import TopLimitDropdown from "../../../../../common/TopLimitDropdown";
import MonthIntervalDropdown from "../../../../../common/MonthIntervalDropdown";
import AccordionComponent from "../../../../../common/Accordion";
import HighchartExportButton from "../../../../../common/HighchartExportButton";
import { initHighchartsModules } from "../../../../../utils/initHighchartsModules";

interface CaseVolumeProps {
  openDropdown: string | null;
  setOpenDropdown: React.Dispatch<React.SetStateAction<string | null>>;
}

const CaseVolumeSummary: React.FC<CaseVolumeProps> = ({
  openDropdown,
  setOpenDropdown,
}) => {
  const [topCount, setTopCount] = useState<number>(5);
  const [monthInterval, setMonthInterval] = useState<string>("1");
  const [topLabel, setTopLabel] = useState<string>("Top 5");
  const [intervalLabel, setIntervalLabel] = useState<string>("This Month");

  const [caseActions, setCaseActions] = useState<any[]>([]);
  const [drilldownData, setDrilldownData] = useState<any[]>([]);
  const [selectedDoctor, setSelectedDoctor] = useState<{
    name: string;
    id: number;
  } | null>(null);

  const [chartOptions, setChartOptions] = useState<Highcharts.Options>({});
  const barColor = "#14B8A6";

  useEffect(() => {
    if (!selectedDoctor) fetchMainChart();
  }, [topCount, monthInterval]);

  useEffect(() => {
    initHighchartsModules();
  }, []);

  const fetchMainChart = async () => {
    try {
      const response = await getQmeDoctorCaseVolume(topCount, monthInterval);
      const data = response.map((item: any) => ({
        ...item,
        qmeDoctorName: item.qmeDoctorName.trim(),
      }));
      setCaseActions(data);
      prepareMainChart(data);
    } catch (error) {
      console.error("Failed to fetch doctor case volume", error);
    }
  };
  const prepareMainChart = (data: any[]) => {
    setChartOptions({
      chart: {
        type: "column",
        height: 300,
        marginBottom: 100, // ← Extra space for labels
      },
      title: { text: undefined },
      xAxis: {
        categories: data.map((d) => d.qmeDoctorName),
        labels: {
          rotation: 0,
          style: {
            fontSize: "12px",
            whiteSpace: "nowrap", // Prevent wrapping
            textOverflow: "ellipsis", // Trim long labels
          },
          autoRotation: [], // Prevent fallback rotation
          reserveSpace: true, // Reserve space for full label
        },
      },
      yAxis: {
        title: { text: "Cases" },
        tickInterval: 20,
      },
      exporting: {
        enabled: true,
        buttons: {
          contextButton: {
            enabled: true,
            symbol: "menu",
          },
        },
      },
      exportData: { enabled: true },
      tooltip: { pointFormat: "<b>{point.y}</b> cases" },
      plotOptions: {
        column: {
          color: barColor,
          pointPadding: 0.2,
          groupPadding: 0.1,
          pointWidth: 25,
          dataLabels: { enabled: true },
          point: {
            events: {
              click: function () {
                const selected = data[this.index];
                setSelectedDoctor({
                  name: selected.qmeDoctorName,
                  id: selected.qmeDoctorContactId,
                });
                fetchDrilldownChart(selected.qmeDoctorContactId);
              },
            },
          },
        },
      },
      credits: { enabled: false },
      legend: { enabled: false },
      series: [
        {
          name: "Cases",
          type: "column",
          data: data.map((d) => d.countOfAssignedCases),
        },
      ],
    });
  };

  const fetchDrilldownChart = async (doctorId: number) => {
    try {
      const response = await getQmeDoctorCaseVolumeByID(
        doctorId,
        monthInterval,
      );
      setDrilldownData(response);
      prepareDrilldownChart(response);
    } catch (error) {
      console.error("Failed to fetch drilldown data", error);
    }
  };

  useEffect(() => {
    if (selectedDoctor) {
      fetchDrilldownChart(selectedDoctor.id);
    }
  }, [monthInterval]);

  const prepareDrilldownChart = (data: any[]) => {
    setChartOptions({
      chart: { type: "bar", height: 300, animation: false },
      title: { text: undefined },
      xAxis: {
        categories: data.map((d) => d.caseStageName),
        labels: { style: { fontSize: "12px" } },
      },
      yAxis: {
        title: { text: "" },
      },
      exporting: {
        enabled: true,
        buttons: {
          contextButton: {
            enabled: true, // Keep it enabled so it renders
            symbol: "menu",
          },
        },
      },

      exportData: {
        enabled: true,
      },
      tooltip: { pointFormat: "<b>{point.y}</b> cases" },
      plotOptions: {
        bar: {
          color: "#6366F1",
          dataLabels: { enabled: true },
        },
      },
      credits: { enabled: false },
      legend: { enabled: false },
      series: [
        {
          name: "Cases",
          type: "bar",
          data: data.map((d) => d.countOfAssignedCasesByStage),
        },
      ],
    });
  };

  const handleTopChange = (value: number, label: string) => {
    setTopCount(value);
    setTopLabel(label);
  };

  const handleIntervalChange = (value: string | number, label: string) => {
    setMonthInterval(String(value));
    setIntervalLabel(label);
  };

  const [caseVolumeKey, setCaseVolumeKey] = useState<string | null>("0");

  const handleBodyAccordionSelect = (key: string | null) => {
    // Prevent collapsing if the same key is clicked
    if (caseVolumeKey === key) {
      return; // do nothing
    }
    setCaseVolumeKey(key || "0");
  };

  const volumeAccordion = (
    items: { title: JSX.Element; content: JSX.Element }[],
  ) => (
    <AccordionComponent
      items={items.map((item, index) => ({
        ...item,
      }))}
      activeKey={caseVolumeKey}
      onSelect={handleBodyAccordionSelect}
    />
  );

  const chartComponentRef = useRef<HighchartsReact.RefObject>(null);

  useEffect(() => {
    const closeAll = () => setOpenDropdown(null);
    window.addEventListener("closeAllDropdowns", closeAll);
    return () => window.removeEventListener("closeAllDropdowns", closeAll);
  }, []);

  return (
    <div className="bg-white rounded-lg shadow-md">
      {volumeAccordion([
        {
          title: (
            <div
              className="d-flex  align-items-center w-100 "
              onClick={(e) => e.stopPropagation()}
            >
              <span className="me-auto">
                {selectedDoctor
                  ? `Cases for ${selectedDoctor.name}`
                  : "Case Volume By Doctor"}
              </span>

              <div className="d-flex gap-2">
                {!selectedDoctor && (
                  <>
                    <TopLimitDropdown
                      title={topLabel}
                      onSelect={handleTopChange}
                      show={openDropdown === "volume"}
                      onToggle={(isOpen) =>
                        setOpenDropdown(isOpen ? "volume" : null)
                      }
                    />
                  </>
                )}
                {selectedDoctor && (
                  <button
                    className="btn d-flex align-items-center btn-text-border p-1 py-0 me-3"
                    onClick={() => {
                      setSelectedDoctor(null);
                      fetchMainChart(); // <-- Explicitly reload main chart on Back
                    }}
                    style={{ background: "#f4f8fe" }}
                  >
                    <span className="material-symbols-outlined pageinfo">
                      arrow_left_alt
                    </span>
                    Back
                  </button>
                )}
                <MonthIntervalDropdown
                  title={intervalLabel}
                  onSelect={handleIntervalChange}
                  show={openDropdown === "volumeMonth"}
                  onToggle={(isOpen) =>
                    setOpenDropdown(isOpen ? "volumeMonth" : null)
                  }
                />
              </div>
              <HighchartExportButton chartRef={chartComponentRef} />
            </div>
          ),
          content: (
            <>
              {caseActions.length === 0 ? (
                <div className="text-center">
                  {/* <img src="/noChart.png" alt="noChart" className="nodata" /> */}
                  <div className="nodata_wrapper">
                    <span className="material-symbols-outlined">
                      bar_chart_off
                    </span>{" "}
                    No Data Available
                  </div>
                </div>
              ) : (
                <HighchartsReact
                  highcharts={Highcharts}
                  options={chartOptions}
                  ref={chartComponentRef}
                />
              )}
            </>
          ),
        },
      ])}
    </div>
  );
};

export default CaseVolumeSummary;
